export const RAW_IMAGE_EXTENSIONS = ['.dng', '.cr2', '.nef', '.orf', '.rw2', '.crw'];

export const isRawImageFormat = (fileName: string): boolean => {
  return RAW_IMAGE_EXTENSIONS.some(ext => fileName.toLowerCase().endsWith(ext));
};

export const ALLOWED_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export const MAX_FILE_SIZE_MB = 5;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

export const resizeImage = async (file: File, maxSizeMB: number = MAX_FILE_SIZE_MB): Promise<File> => {
  return new Promise((resolve, reject) => {
    // If file is already small enough, return it
    if (file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }

    // Only resize images
    if (!isImageFile(file)) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = event => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate new dimensions to reduce file size
        // Start with a quality reduction factor
        const quality = 0.9;
        let { width } = img;
        let { height } = img;

        // If image is very large, reduce dimensions
        const maxDimension = 2048;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob and check size iteratively
        const tryCompress = (currentQuality: number) => {
          canvas.toBlob(
            blob => {
              if (!blob) {
                reject(new Error('Failed to create blob'));
                return;
              }

              // If still too large and quality can be reduced further, try again
              if (blob.size > maxSizeMB * 1024 * 1024 && currentQuality > 0.3) {
                tryCompress(currentQuality - 0.1);
              } else {
                // Create new file from blob
                const resizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              }
            },
            'image/jpeg',
            currentQuality,
          );
        };

        tryCompress(quality);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};
